import React, { Component } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import Seo from '../Seo/Seo';

const styles = theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at top, #1c2c2f 0%, #0f1116 45%, #0a0c10 100%)',
    padding: theme.spacing(4),
  },
  card: {
    textAlign: 'left',
    background: 'rgba(20, 24, 28, 0.86)',
    color: '#f5f7fa',
    borderRadius: 24,
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(16px)',
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  heading: {
    color: '#72f7c4',
    fontWeight: 700,
  },
  subheader: {
    color: 'rgba(245, 247, 250, 0.7)',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  helper: {
    color: 'rgba(245, 247, 250, 0.7)',
    marginBottom: theme.spacing(2),
  },
  form: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  field: {
    width: '100%',
  },
  input: {
    color: '#f5f7fa',
  },
  cssLabel: {
    color: 'rgba(245, 247, 250, 0.7)',
    '&$cssFocused': {
      color: '#72f7c4',
    },
  },
  cssOutlinedInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 14,
    '&$cssFocused $notchedOutline': {
      borderColor: '#72f7c4',
      borderWidth: 2,
    },
  },
  icon: {
    color: '#d0fced',
  },
  cssFocused: {},
  notchedOutline: { borderColor: 'rgba(255,255,255,0.16)' },
  button: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.4, 0),
    fontWeight: 600,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #72f7c4 0%, #4ad6ff 100%)',
    color: '#0a0c10',
    textTransform: 'none',
  },
  link: {
    color: '#9cefd2',
    marginTop: theme.spacing(1),
    display: 'inline-block',
  },
  errorMessage: {
    marginTop: theme.spacing(1),
    color: '#ffdbdb',
    background: 'rgba(255, 108, 108, 0.14)',
    border: '1px solid rgba(255, 108, 108, 0.45)',
    borderRadius: 10,
    padding: theme.spacing(1.5),
  },
  successMessage: {
    marginTop: theme.spacing(1),
    color: '#ccffef',
    background: 'rgba(114, 247, 196, 0.12)',
    border: '1px solid rgba(114, 247, 196, 0.35)',
    borderRadius: 10,
    padding: theme.spacing(1.5),
  }
});

class ResetPasswordPage extends Component {
  state = {
    token: '',
    password: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
    isSubmitting: false,
    success: false,
    error: '',
  };

  componentDidMount() {
    const searchParams = new URLSearchParams(this.props.location.search);
    const token = searchParams.get('token') || '';
    this.setState({ token });
  }

  validatePassword = () => {
    const { password, confirmPassword } = this.state;

    if (password.length < 8 || !/[A-Z]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      return 'Password must be at least 8 characters and include at least one uppercase letter and one special character.';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    return '';
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.state.isSubmitting) {
      return;
    }

    const validationError = this.validatePassword();
    if (validationError) {
      this.setState({ error: validationError });
      return;
    }

    this.setState({ isSubmitting: true, error: '' });

    try {
      await axios.post('/api/auth/reset-password', {
        token: this.state.token,
        password: this.state.password,
      });

      this.setState({ success: true, isSubmitting: false });
      setTimeout(() => {
        this.props.history.push('/login');
      }, 2000);
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Unable to reset password. Please request a new link.';

      this.setState({ isSubmitting: false, error: message });
    }
  }

  renderMissingToken() {
    const { classes, history } = this.props;

    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography variant="h4" component="h1" className={classes.heading}>
            Reset link required
          </Typography>
          <Typography className={classes.subheader}>
            We could not find a reset token in your link.
          </Typography>
          <Link component="button" className={classes.link} onClick={() => history.push('/forgot-password')}>
            Request a new reset link
          </Link>
        </CardContent>
      </Card>
    );
  }

  render() {
    const { classes, history } = this.props;

    return (
      <Box className={classes.root}>
        <Seo
          title="Reset Password | Trivia Weekend"
          description="Choose a new password for your Trivia Weekend account."
          canonicalPath="/#/reset-password"
          ogType="website"
        />
        <Grid container justify="center">
          <Grid item xs={12} sm={10} md={7} lg={5}>
            {!this.state.token ? this.renderMissingToken() : (
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <Typography variant="h4" component="h1" className={classes.heading}>
                    Reset your password
                  </Typography>
                  <Typography variant="subtitle1" className={classes.subheader}>
                    Enter and confirm your new password.
                  </Typography>
                  <Typography className={classes.helper}>
                    Password must be at least 8 characters and include at least one uppercase letter and one special character.
                  </Typography>

                  <form onSubmit={this.handleSubmit} className={classes.form}>
                    <TextField
                      type={this.state.showPassword ? 'text' : 'password'}
                      label="New Password"
                      className={classes.field}
                      value={this.state.password}
                      onChange={(event) => this.setState({ password: event.target.value })}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        className: classes.input,
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              className={classes.icon}
                              onClick={() => this.setState({ showPassword: !this.state.showPassword })}
                              edge="end"
                            >
                              {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      InputLabelProps={{
                        className: classes.cssLabel,
                        shrink: true
                      }}
                    />

                    <TextField
                      type={this.state.showConfirmPassword ? 'text' : 'password'}
                      label="Confirm New Password"
                      className={classes.field}
                      value={this.state.confirmPassword}
                      onChange={(event) => this.setState({ confirmPassword: event.target.value })}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        className: classes.input,
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              className={classes.icon}
                              onClick={() => this.setState({ showConfirmPassword: !this.state.showConfirmPassword })}
                              edge="end"
                            >
                              {this.state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      InputLabelProps={{
                        className: classes.cssLabel,
                        shrink: true
                      }}
                    />

                    <Button className={classes.button} type="submit" disabled={this.state.isSubmitting || this.state.success}>
                      {this.state.isSubmitting ? 'Updating…' : 'Reset password'}
                    </Button>
                  </form>

                  {this.state.error && (
                    <Typography className={classes.errorMessage}>
                      {this.state.error}
                    </Typography>
                  )}

                  {this.state.success && (
                    <Typography className={classes.successMessage}>
                      Password reset successful. Redirecting to login…
                    </Typography>
                  )}

                  <Link component="button" className={classes.link} onClick={() => history.push('/forgot-password')}>
                    Request new link
                  </Link>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default withRouter(withStyles(styles)(ResetPasswordPage));
